/**
 * 跟读进度的本机缓存层.
 *
 * 进度写库有 5s 节流, 写库前先在 localStorage 留一份:
 * - 同一台设备刷新或重进跟读页时, 不等网络就能续上;
 * - 跨设备一致性由后端记录兜底 (见 pages/practice.ts 的 resumeProgress).
 *
 * 键按用户隔离, 避免共享设备上不同账号互相续播。
 */

import type { CourseProgress } from '../types/api'

const KEY_PREFIX = 'oma-gendu:progress'

export interface LocalProgress {
  positionSeconds: number
  sentenceId: number
  /** 本地写入时间, epoch ms; 与后端 updated_at 比较, 决定续播用哪一份 */
  updatedAt: number
}

function storageKey(userId: number, courseId: number): string {
  return `${KEY_PREFIX}:${userId}:${courseId}`
}

export function readLocalProgress(userId: number, courseId: number): LocalProgress | null {
  try {
    const raw = localStorage.getItem(storageKey(userId, courseId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LocalProgress>
    if (typeof parsed.positionSeconds !== 'number' || typeof parsed.sentenceId !== 'number') {
      return null
    }
    return {
      positionSeconds: parsed.positionSeconds,
      sentenceId: parsed.sentenceId,
      updatedAt: Number(parsed.updatedAt) || 0,
    }
  } catch {
    // 损坏的缓存当作没有, 后端记录仍然可用
    return null
  }
}

export function saveLocalProgress(
  userId: number,
  courseId: number,
  progress: LocalProgress,
): void {
  try {
    localStorage.setItem(storageKey(userId, courseId), JSON.stringify(progress))
  } catch {
    // 隐私模式等写入失败可忽略: 后端同步照常
  }
}

/** 后端记录 → 本地缓存, 时间戳用于下次比较 */
export function toLocalProgress(progress: CourseProgress): LocalProgress {
  return {
    positionSeconds: progress.position_seconds,
    sentenceId: progress.sentence_id,
    updatedAt: Date.parse(progress.updated_at) || Date.now(),
  }
}
